import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

// ============ Styled Components ============
const Section = styled.section`
    padding: 80px 20px;
    max-width: 1400px;
    margin: 0 auto;
`;

const Title = styled.h2`
    text-align: center;
    font-size: clamp(2rem, 3vw, 2.6rem);
    margin-bottom: 50px;
    color: #2c3e50;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 22px;
    margin-top: 24px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled(motion.article)`
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    padding: 24px;
    display: flex;
    flex-direction: column;
`;

const CardTitle = styled.h3`
    font-size: 1.4rem;
    margin-bottom: 12px;
    color: #c58e50;
`;

const SubHeading = styled.h4`
    font-size: 1.1rem;
    margin: 16px 0 8px;
    color: #2c3e50;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 20px;
  line-height: 1.6;
  color: #444;
`;

const ProList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  li::before {
    content: "✔ ";
    color: green;
    font-weight: bold;
  }
`;

const ContraList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  li::before {
    content: "✖ ";
    color: #b30000;
    font-weight: bold;
  }
`;

// Анимация
const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// ============ Данни ============
const materials = [
    {
        title: "EPDM „американски тип“",
        features: [
            "100% синтетичен каучук, обикновено хомогенен",
            "Изключително дълъг живот 50+ години",
            "Калтнафтфюгунг чрез мрежово свързване",
            "В големи платна без шевове",
            "Гъвкав от -40 до +120°C",
            "Изключително еластичен ≥ 300%"
        ],
        pro: [
            "Много дълъг експлоатационен живот",
            "Малко или никакви фуги",
            "Ниско собствено тегло",
            "Устойчив и екологичен благодарение на дългия живот",
            "Не изисква задължително наклон",
            "Съвместим с битум",
            "Много бърз монтаж"
        ],
        contra: [
            "Големите платна могат да са тежки",
            "Почти винаги само в черно"
        ]
    },
    {
        title: "EPDM „европейски тип“",
        features: [
            "100% синтетичен каучук, обикновено хомогенен",
            "Изключително дълъг живот 50+ години",
            "Горещо заваряване (или лепене)",
            "Тесни ленти / предварително изработени платна",
            "Покриви и в 2D и 3D",
            "Гъвкав от -40 до +120°C",
            "Изключително еластичен ≥ 300%"
        ],
        pro: [
            "Малко или никакви фуги",
            "Ниско собствено тегло",
            "Устойчив и екологичен благодарение на дългия живот",
            "Съвместим с битум",
            "Като предварително конфигурирано платно – бърз монтаж"
        ],
        contra: [
            "Фугиране на обекта само с опит",
            "Големите платна могат да станат тежки"
        ]
    },
    {
        title: "EPDM многослоен (европейски)",
        features: [
            "Горен слой EPDM, разделителен слой, битумна подложка",
            "Изключително дълъг живот 50+ (горен слой)",
            "Фугиране чрез горещо заваряване",
            "Тесни ленти 1 до 2 м ширина"
        ],
        pro: [
            "Ниско тегло",
            "Устойчив и екологичен благодарение на дългия живот",
            "Съвместим с битум",
            "Често наличен и като самозалепващ"
        ],
        contra: [
            "Често се изисква грунд",
            "Фугиране на обекта само с опит",
            "Само в черно"
        ]
    },
    {
        title: "Битумни мембрани",
        features: [
            "Често полимер-модифициран битум (PYE/PYP)",
            "Тесни ленти 80-100 см, много фуги",
            "Фугиране с открит пламък",
            "Многослоен монтаж (минимум 2)",
            "UV защита често с посипка"
        ],
        pro: ["Добре познати на покриварите", "Висока наличност на пазара"],
        contra: [
            "Неустойчиви – кратък живот",
            "Метални детайли трябва да се покриват",
            "Опасен отпадък",
            "Само за професионалисти",
            "Много фуги"
        ]
    },
    {
        title: "PVC мембрани",
        features: [
            "Поливинилхлорид, обикновено с армировка",
            "Фугиране с горещ въздух",
            "Фугиране със специални течности",
            "Тесни ролки до 2 м"
        ],
        pro: ["Много цветове", "Относително евтини"],
        contra: [
            "Неустойчиви – кратък живот",
            "Често не са UV стабилни",
            "Чувствителни към миграция на пластификатори",
            "Изискват допълнителни детайли"
        ]
    },
    {
        title: "FPO/TPO мембрани",
        features: [
            "Гъвкави полиолефини (FPO) с армировка",
            "Фугиране чрез горещо заваряване"
        ],
        pro: ["Дълъг живот", "Много цветове", "Съвместими с битум"],
        contra: [
            "Много фуги",
            "Необходими допълнителни детайли",
            "Относително твърди, по-малко гъвкави",
            "Не са подходящи за DIY"
        ]
    },
    {
        title: "Течни пластмаси",
        features: [
            "1К или 2К пластмаси",
            "Обикновено с армировка",
            "Задължителна подготовка на основата",
            "Дебелина на слоя >2 мм"
        ],
        pro: [
            "Напълно безшевни",
            "Не изискват формовани детайли",
            "Подходящи за сложни покриви"
        ],
        contra: [
            "Не са подходящи за DIY",
            "Много време за полагане",
            "Задължителна подготовка на основата"
        ]
    },
    {
        title: "Метални покриви",
        features: [
            "Мед, цинк, алуминий или стомана",
            "Обикновено профилирани листове",
            "Фугиране чрез стоящ фалц / припокриване"
        ],
        pro: ["Красива естетика", "Много дълъг живот"],
        contra: [
            "Сложни детайли",
            "Висок разход за планиране и монтаж",
            "Не са подходящи за сложни покриви",
            "Не са подходящи за DIY"
        ]
    }
];

// ============ Компонент ============
export default function RoofMaterialsSection() {
    return (
        <Section>
            <Title>Сравнение на покривни материали</Title>
            <Grid>
                {materials.map((mat, i) => (
                    <Card key={i} variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                        <CardTitle>{mat.title}</CardTitle>

                        <SubHeading>Характеристики</SubHeading>
                        <List>
                            {mat.features.map((f, idx) => (
                                <li key={idx}>{f}</li>
                            ))}
                        </List>

                        <SubHeading>Предимства</SubHeading>
                        <ProList>
                            {mat.pro.map((p, idx) => (
                                <li key={idx}>{p}</li>
                            ))}
                        </ProList>

                        <SubHeading>Недостатъци</SubHeading>
                        <ContraList>
                            {mat.contra.map((c, idx) => (
                                <li key={idx}>{c}</li>
                            ))}
                        </ContraList>
                    </Card>
                ))}
            </Grid>
        </Section>
    );
}
